import React, { useState, useEffect } from "react";
import { Modal, Table, Button, Tabs, message, Space, Typography } from "antd";
import styles from "./deviceTable.module.scss";
import { useTranslation } from "react-i18next";
import { setAlarmTableVisible } from "../store/topologySlice";
import { useAppSelector, useAppDispatch } from "store/hook";
import { TimestampToDate } from "../../../Utility/Fetch";
import { useAlarmLog, useClearAlarmLog } from "../queries";
import { useConfig } from "Components/Navbar/queries";
import { MCSVtoModel } from "Utility/mode_name";
import SystemLog from "./DeviceTableF_SystemLog";
import MQTTDiagnose from "./Diagnosis";
import DigitalOutput from "./Digital_Output";

interface ItableData {
  category: string;
  key: string;
}
export type TabPaneKey = "currentAlarm" | "alarmHistory" | "systemLog" | "digitalOutput" | "diagnosis";

const { Text } = Typography;
const { TabPane } = Tabs;
// 顯示 Overview / Devices Overview 列表中 Info 的 Health icon 彈出內容

const HealthModal = React.memo(() => {
  const disp = useAppDispatch();
  const { deviceRecord, alarmTableVisible } = useAppSelector((state) => state.topology);

  const { level, time_zone } = useAppSelector((state) => state.global);
  const [currentTab, setCurrentTab] = useState<TabPaneKey>("currentAlarm");
  const [deviceID, setDeviceID] = useState("");
  const { t } = useTranslation();
  const { data: config } = useConfig();
  const { data, isFetching: alarmLogFetching } = useAlarmLog(deviceRecord?.id, alarmTableVisible);
  const { mutateAsync: clearAlarmMutation, isLoading: clearAlarmLoading } = useClearAlarmLog(deviceRecord?.id);
  const loading = alarmLogFetching || clearAlarmLoading;

  const modelMap = config?.modelMap || {};
  // 將設備分類 為 "M301" | "M330" | "M331" | "M335" | "M350" | "M351" | "M352" | undefined
  const strmModel = MCSVtoModel(modelMap[deviceRecord?.model]); //MCSV

  const ChangeTab = (value: TabPaneKey) => {
    setCurrentTab(value);
  };

  const modelOnClose = () => {
    disp(setAlarmTableVisible(false));
    setCurrentTab("currentAlarm");
  };

  const columns: any[] = [
    {
      title: t("ISMS.Trigger"),
      dataIndex: "category",
      responsive: ["md"],
      filters: [
        {
          text: "NMS",
          value: "NMS",
        },
        {
          text: "Device",
          value: "Device",
        },
      ],
      onFilter: (value: string, record: ItableData) => record.category.indexOf(value) === 0,
    },
    {
      title: t("ISMS.Dashboard"),
      dataIndex: "level",
      responsive: ["md"],
      render: (text: string) => {
        switch (text) {
          case "DISCONNECTED":
            return "OFFLINE";
          case "FATAL":
            return "CRITICAL";
          case "ERROR":
            return "CRITICAL";
          case "WARN":
            return "WARNING";
          default:
            return "Good";
        }
      },
    },
    {
      title: t("ISMS.Level"),
      responsive: ["md"],
      dataIndex: "level",
    },
    {
      title: t("ISMS.Message"),
      dataIndex: "message",
    },
    {
      title: t("ISMS.TrigerTime"),
      dataIndex: "trigger_time",

      sorter: (a: any, b: any) => {
        return a.trigger_time - b.trigger_time;
      },
      defaultSortOrder: "descend",
      render: (text: number) => {
        return TimestampToDate(text * 1000, time_zone);
      },
    },
    {
      title: t("ISMS.RecoverTime"),
      dataIndex: "recover_time",

      sorter: (a: any, b: any) => {
        return a.recover_time - b.recover_time;
      },
      sortDirections: ["descend"],
      defaultSortOrder: "descend",
      render: (text: number) => {
        if (text === 0) {
          return null;
        }
        return TimestampToDate(text * 1000, time_zone);
      },
    },
  ];

  useEffect(() => {
    setDeviceID(deviceRecord?.id);
  }, [deviceRecord]);

  return (
    <Modal
      className={styles.healthModel}
      title={
        <Space>
          <Text>{t("ISMS.Health")}</Text>
          <Text className={styles.healthModel__deviceTitle}>{deviceRecord?.name_or_id}</Text>
        </Space>
      }
      visible={alarmTableVisible}
      onCancel={modelOnClose}
      centered={true}
      destroyOnClose={true}
      footer={[
        currentTab === "alarmHistory" && (
          <Button
            loading={loading}
            disabled={level === "get" || !data?.alarmLog?.length}
            danger
            key="clear"
            type="primary"
            onClick={() => {
              clearAlarmMutation(deviceRecord.id)
                .then(() => {
                  message.success(t("ISMS.Success"));
                })
                .catch(() => {
                  message.error(t("ISMS.Fail"));
                });
            }}
          >
            {t("ISMS.ClearLog")}
          </Button>
        ),
        <Button loading={loading} key="confirm" type="primary" onClick={modelOnClose}>
          {t("ISMS.Confirm")}
        </Button>,
      ]}
    >
      <Tabs type="card" defaultActiveKey="currentAlarm" onChange={(value) => ChangeTab(value as TabPaneKey)}>
        <TabPane tab={t("ISMS.Alarm")} key="currentAlarm">
          <Table
            loading={loading}
            dataSource={data?.currentAlarm || []}
            columns={columns}
            scroll={{ y: 400 }}
            size="middle"
          />
        </TabPane>
        <TabPane tab={t("ISMS.History")} key="alarmHistory">
          <Table
            loading={loading}
            dataSource={data?.alarmLog || []}
            columns={columns}
            scroll={{ y: 400 }}
            size="middle"
          />
        </TabPane>
        {/* 顯示 SystemLog 功能 限定 M331 才會顯示 log */}
        {strmModel === "M331" && (
          <TabPane tab={t("ISMS.SystemLog")} key="systemLog">
            <SystemLog />
          </TabPane>
        )}
        {/* Digital Output 只有 301 (300 系列) 有 */}
        {strmModel === "M301" && (
          <TabPane tab={t("ISMS.DigitalOutput")} key="digitalOutput">
            <DigitalOutput deviceID={deviceRecord?.id} cid={deviceRecord?.cid} />
          </TabPane>
        )}
        <TabPane tab={t("ISMS.Diagnosis")} key="diagnosis">
          <MQTTDiagnose deviceID={deviceID} cid={deviceRecord?.cid} currentTab={currentTab} />
        </TabPane>
      </Tabs>
    </Modal>
  );
});

export default HealthModal;
